// Search-result copy for library pages.
//
// Google shows roughly 60 characters of a title and the first ~90 characters
// of a description before it stops being read. Our gem pages were spending
// that budget on taxonomy ("— UI Components and Libraries | Hidden Gems
// Library") instead of on what the tool actually is, which is what people
// type. These helpers put the useful words first.

const TITLE_MAX = 60;
const DESC_MAX = 157;

/** Collapse whitespace and strip a trailing separator. */
function tidy(s: string): string {
  return s.replace(/\s+/g, " ").trim().replace(/[.,;:–—-]+$/, "");
}

/**
 * A short "what it is" line: the first sentence or clause of the description,
 * with a leading restatement of the tool's own name removed so the title does
 * not read "Kinetics — Kinetics is a…".
 */
export function shortSummary(
  name: string,
  description: string | null | undefined,
  fallback?: string | null,
): string {
  const raw = tidy(description || "");
  if (!raw) return tidy(fallback || "");
  // Drop "<Name> is a ", "A library that ", etc.
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  let s = raw
    .replace(new RegExp(`^${escaped}\\s+(is|are)\\s+(a|an|the)?\\s*`, "i"), "")
    .replace(/^(a|an|the)\s+/i, "");
  // First sentence only.
  const stop = s.search(/[.!?](\s|$)/);
  if (stop > 20) s = s.slice(0, stop);
  s = tidy(s);
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : tidy(fallback || "");
}

/**
 * Gem <title>: name first, then a plain-English summary, then the brand —
 * dropping the tail pieces rather than letting Google truncate mid-word.
 */
export function gemTitle(item: {
  name: string;
  description?: string | null;
  subcategory?: string | null;
  category?: string | null;
}): string {
  const name = tidy(item.name);
  const summary = shortSummary(
    name,
    item.description,
    item.subcategory || item.category || "",
  );
  const brand = "Vibeary";

  const withAll = summary ? `${name} — ${summary} | ${brand}` : `${name} | ${brand}`;
  if (withAll.length <= TITLE_MAX) return withAll;

  const withoutBrand = summary ? `${name} — ${summary}` : name;
  if (withoutBrand.length <= TITLE_MAX) return withoutBrand;

  // Trim the summary so the tool name always survives, preferring a natural
  // phrase boundary over a mid-thought cut.
  const room = TITLE_MAX - name.length - 4;
  if (summary && room > 12) {
    const trimmed = trimToPhrase(summary, room);
    if (trimmed) return `${name} — ${trimmed}`;
  }
  return name.length <= TITLE_MAX ? name : `${name.slice(0, TITLE_MAX - 1)}…`;
}

// Words a title should never end on — they leave the reader mid-sentence.
const DANGLING =
  /\s+(a|an|the|and|or|of|for|with|to|in|on|by|that|which|whose|where|when|from|as|at|into|over|per|via|built|using|rather|each|every|its|their|one)$/i;

/**
 * Cut a phrase to fit `max`, preferring a comma/colon boundary, then a word
 * boundary, and dropping trailing connective words so it reads finished.
 */
function trimToPhrase(text: string, max: number): string {
  let s = tidy(text);
  if (s.length <= max) return dropDangling(s);
  const window = s.slice(0, max);
  const punct = Math.max(
    window.lastIndexOf(","),
    window.lastIndexOf(";"),
    window.lastIndexOf(":"),
  );
  if (punct >= Math.floor(max * 0.55)) return dropDangling(window.slice(0, punct));
  const space = window.lastIndexOf(" ");
  s = dropDangling(space > 10 ? window.slice(0, space) : window);
  // Cut mid-thought: the ellipsis tells the reader the sentence continues on
  // the page instead of looking like a broken title.
  return s ? `${s}…` : s;
}

function dropDangling(text: string): string {
  let s = tidy(text);
  let guard = 0;
  while (DANGLING.test(s) && guard++ < 6) s = tidy(s.replace(DANGLING, ""));
  return s;
}

/**
 * Gem meta description: the curated "why it's a gem" line first (that is the
 * reason to click), then the plain description as backup.
 */
export function gemDescription(item: {
  name: string;
  why_gem?: string | null;
  description?: string | null;
}): string {
  const primary = tidy(item.why_gem || "");
  const secondary = tidy(item.description || "");
  let text = primary || secondary;
  // If the curated line is very short, top it up with the description so the
  // snippet is not half-empty in the results page.
  if (primary && secondary && primary.length < 80 && !primary.includes(secondary)) {
    const joiner = /[.!?]$/.test(primary) ? " " : ". ";
    text = `${primary}${joiner}${secondary}`;
  }
  if (!text) text = "A hand-curated Hidden Gem from The Vibeary.";
  if (text.length <= DESC_MAX) return text;
  const cut = text.slice(0, DESC_MAX);
  const space = cut.lastIndexOf(" ");
  return `${tidy(space > 100 ? cut.slice(0, space) : cut)}…`;
}

const OSS_LICENSES = [
  "mit",
  "apache",
  "bsd",
  "isc",
  "mpl",
  "gpl",
  "agpl",
  "lgpl",
  "unlicense",
  "cc0",
  "public domain",
];

/** True when the stated license means the thing is free to use. */
export function isFreeLicense(license: string | null | undefined): boolean {
  if (!license) return false;
  const l = license.toLowerCase();
  return OSS_LICENSES.some((k) => l.includes(k));
}
